import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { gsap } from '../../../motion/gsap';
import { QUERIES } from '../../../motion/motion-env';
import { MotionService } from '../../../motion/motion.service';
import { translateLive } from '../../../services/i18n-live';

/** One "Why choose us" entry as stored in i18n (`about.chooseUs.content`). */
interface ChooseUsItem {
  title: string;
  describtion: string;
}

@Component({
  selector: 'app-about',
  templateUrl: './about.component.html',
  styleUrl: './about.component.css',
})
export class About implements OnInit, OnDestroy {
  whyChooseUs: ChooseUsItem[] = [];

  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly motion = inject(MotionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private wiped = false;

  constructor() {
    afterNextRender(() => this.motion.outside(() => this.setupMotion()));
  }

  ngOnInit(): void {
    translateLive<ChooseUsItem[]>(this.translate, 'about.chooseUs.content')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((items) => {
        this.whyChooseUs = Array.isArray(items) ? items.map(toCard) : [];
      });

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.translate.use(params['lang'] || 'am');
    });
  }

  ngOnDestroy(): void {
    this.mm?.revert();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private setupMotion(): void {
    const mm = (this.mm = gsap.matchMedia(this.host));
    mm.add({ motion: QUERIES.motion, mobile: QUERIES.mobile }, (ctx) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion']) return;
      const stopSync = this.syncCardCascade();
      const restoreFrame = this.setupWiper(!!conditions['mobile']);
      return () => {
        stopSync();
        restoreFrame();
      };
    });
  }

  /**
   * Windshield-wiper reveal of the instructor photo, once: a circular clip sweeps up from the
   * frame's bottom-left corner (bottom centre on mobile) while the image settles from a uniform
   * 1.15 scale. The image's parallax (yPercent) is a separate transform component, so both run.
   */
  private setupWiper(mobile: boolean): () => void {
    const frame = this.host.querySelector<HTMLElement>('.ab-frame');
    const img = frame?.querySelector<HTMLImageElement>('img');
    if (!frame || !img || this.wiped) return () => {};

    // Already scrolled past, or the page was rebuilt by a language switch: show it as is.
    const rect = frame.getBoundingClientRect();
    if (rect.bottom <= 0 || (this.motion.recentLanguageSwitch() && rect.top < window.innerHeight)) {
      this.wiped = true;
      return () => {};
    }

    const at = mobile ? 'at 50% 100%' : 'at 0% 100%';
    gsap
      .timeline({
        scrollTrigger: {
          trigger: frame,
          start: 'top 75%',
          once: true,
          onEnter: () => {
            this.wiped = true;
          },
        },
      })
      .fromTo(
        frame,
        { clipPath: `circle(0% ${at})` },
        { clipPath: `circle(${mobile ? 150 : 142}% ${at})`, duration: 1.1, ease: 'power3.inOut', clearProps: 'clipPath' },
        0
      )
      // Not rendered before the trigger (the clip hides the image until then anyway): the image's
      // parallax tween saves the image's style for its revert when it is created, and it must
      // save the clean state, not this scale, or a media-query flip would restore scale 1.15.
      .fromTo(img, { scale: 1.15 }, { scale: 1, duration: 1.4, ease: 'expo.out', immediateRender: false }, 0);

    // Never leave the photo clipped after a revert (destroy or a media-query flip mid-wipe).
    return () => gsap.set(frame, { clearProps: 'clipPath' });
  }

  /**
   * The check draw and the text rise inside each card are CSS transitions keyed to the
   * RevealDirective classes. A batched card starts its own tween up to ~.5s after it gets
   * `is-in`, so copy that delay into `--ab-delay` for the inner transitions to follow it.
   * MutationObserver callbacks run as a microtask after the batch flush, before the next
   * style recalc, so the delay is in place when the transitions start.
   */
  private syncCardCascade(): () => void {
    const list = this.host.querySelector<HTMLElement>('.ab-cards');
    if (!list) return () => {};
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        const card = record.target as HTMLElement;
        if (!card.classList.contains('ab-card') || !card.classList.contains('is-in')) continue;
        const tween = gsap.getTweensOf(card).find((t) => t.duration() > 0);
        card.style.setProperty('--ab-delay', `${tween ? tween.delay() : 0}s`);
      }
    });
    observer.observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      // On a live switch to reduced motion, land the draws already in flight instead of letting
      // them run out (a running CSS transition is not cancelled when its rule goes away).
      list.getAnimations?.({ subtree: true }).forEach((animation) => animation.finish());
    };
  }
}

/** Card titles used to run into the description ("Title: text"); on their own line the trailing colon goes. */
function toCard(item: ChooseUsItem): ChooseUsItem {
  return { ...item, title: (item.title ?? '').trim().replace(/\s*[:՝]$/, '') };
}
